import {
  Components,
  createTheme,
  PaletteColor,
  ThemeProvider,
} from "@mui/material/styles";
import { Theme } from "@mui/system";
import React, { useMemo } from "react";
import { useViewMode } from "./ViewMode/useViewMode";
import { ViewModeType } from "./ViewMode/ViewModeProvider";

interface ColorOptions {
  error: Pick<PaletteColor, "main">;
  primary: PaletteColor;
  backgroundColor: string;
  paperBackgroundColor?: string;
  textColor: string;
  mode: "light" | "dark";
  components?: Components<Omit<Theme, "components">>;
}

const viewModeOptions: Record<ViewModeType, ColorOptions> = {
  light: {
    error: {
      main: "#CC0033",
    },
    primary: {
      main: "#008128",
      dark: "#005300",
      light: "#4ab255",
      contrastText: "#FFFFFF",
    },
    backgroundColor: "#fbfdfc",
    textColor: "#555555",
    mode: "light",
  },
  contrast: {
    error: {
      main: "#FFBDCB",
    },
    primary: {
      main: "#FFE600",
      dark: "#c7b400",
      light: "#ffff55",
      contrastText: "#000000",
    },
    backgroundColor: "#000000",
    paperBackgroundColor: "#000000",
    textColor: "#FFE600",
    mode: "dark",
    components: {
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: "#FFE600",
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            borderColor: "#FFE600",
          },
        },
      },
    },
  },
};

export const getMaterialTheme = (mode: ViewModeType, baseFontSize: number) => {
  const options = viewModeOptions[mode];
  return createTheme({
    palette: {
      mode: options.mode,
      text: {
        primary: options.textColor,
      },
      background: {
        default: options.backgroundColor,
        ...(options.paperBackgroundColor
          ? { paper: options.paperBackgroundColor }
          : {}),
      },
      primary: options.primary,
      error: {
        main: options.error.main,
      },
    },
    spacing: 8,
    shape: {
      borderRadius: 10,
    },
    typography: {
      fontFamily: "'Poppins', sans-serif",
      fontSize: baseFontSize,
    },
    components: {
      ...(options.components ?? {}),
      MuiAlert: {
        styleOverrides: {
          root: {
            alignItems: "center",
          },
          action: {
            paddingTop: 0,
            paddingBottom: 0,
          },
        },
      },
      MuiIconButton: {
        defaultProps: {
          color: "primary",
        },
      },
      MuiButton: {
        defaultProps: {
          variant: "contained",
        },
        styleOverrides: {
          root: ({ theme }) => ({
            padding: theme.spacing(1, 3),
            textTransform: "none",
            borderWidth: 2,
            "&:hover": {
              borderWidth: 2,
            },
          }),
        },
      },
      MuiLink: {
        styleOverrides: {
          root: ({ theme }) => ({
            transition: theme.transitions.create([
              "color",
              "text-decoration-color",
            ]),
            "&:hover": {
              color: theme.palette.primary.dark,
            },
          }),
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 2,
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: theme.spacing(2, 3),
            "&:last-child": {
              paddingBottom: theme.spacing(2),
            },
          }),
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            textAlign: "center",
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&>.MuiListItemButton-root": {
              paddingRight: theme.spacing(6),
            },
          }),
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: theme.spacing(1, 2),
          }),
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
          },
        },
      },
    },
  });
};
interface MaterialThemeProps {
  children: React.ReactNode;
}

const MaterialTheme = ({ children }: MaterialThemeProps) => {
  const { mode, font } = useViewMode();
  const materialTheme = useMemo(
    () => getMaterialTheme(mode, font),
    [mode, font]
  );

  return <ThemeProvider theme={materialTheme}>{children}</ThemeProvider>;
};

export default MaterialTheme;
